import React, { useState } from 'react';
import SiteFooter from '../components/SiteFooter';
import { useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import { Star, Loader2 } from 'lucide-react';

const RateOrder: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const params = useParams<{ orderNum?: string; phoneSuffix?: string }>();
  // 支援兩種連結格式：/rate/:orderNum/:phoneSuffix（WhatsApp 按鈕用）及 /rate?order=...&phoneSuffix=...
  const orderNum = params.orderNum || searchParams.get('order') || '';
  const initialSuffix = params.phoneSuffix || searchParams.get('phoneSuffix') || '';

  const [phoneSuffix, setPhoneSuffix] = useState(initialSuffix);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!orderNum) {
      setError(t('order:rate.orderNumMissing'));
      setStatus('error');
      return;
    }
    if (rating < 1 || rating > 5) {
      setError(t('order:rate.selectStars'));
      setStatus('error');
      return;
    }
    const suffix = phoneSuffix.replace(/\D/g, '');
    if (suffix.length !== 4) {
      setError(t('order:rate.enterLast4'));
      setStatus('error');
      return;
    }

    setStatus('submitting');
    try {
      const res = await apiFetch('/api/public/orders/rate-by-order-num', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNum, rating, review: review.trim(), phoneSuffix: suffix }),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('order:rate.submitFailed'));
        setStatus('error');
      }
    } catch {
      setError(t('order:rate.network'));
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#FFF7ED] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-orange-100 p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-stone-800 mb-2">{t('order:rate.thanksTitle')}</h1>
          <p className="text-stone-600">{t('order:rate.thanksBody')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-[#FFF7ED] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg border border-orange-100 p-6 md:p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold text-stone-800 mb-2 text-center">{t('order:rate.title')}</h1>
        <p className="text-stone-600 text-center mb-6">{t('order:rate.orderNumLabel', { orderNum: orderNum || '—' })}</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-base font-bold text-stone-700 mb-2">{t('order:rate.last4Label')}</label>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={4}
              value={phoneSuffix}
              onChange={(e) => setPhoneSuffix(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder={t('order:rate.last4Placeholder')}
              className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition min-h-[56px]"
            />
          </div>

          <div>
            <label className="block text-base font-bold text-stone-700 mb-2">{t('order:rate.ratingLabel')}</label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    size={40}
                    className={
                      (hoverRating ? star <= hoverRating : star <= rating)
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-stone-300'
                    }
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-base font-bold text-stone-700 mb-2">{t('order:rate.commentLabel')}</label>
            <textarea
              value={review}
              onChange={(e) => setReview(e.target.value)}
              placeholder={t('order:rate.commentPlaceholder')}
              rows={4}
              className="w-full p-4 text-lg border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition resize-none"
            />
          </div>

          {error && <p className="text-base text-red-600 font-bold">{error}</p>}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full bg-[#C2410C] text-white text-lg font-bold py-4 rounded-xl hover:bg-[#EA580C] transition disabled:opacity-50 flex items-center justify-center gap-2 min-h-[56px]"
          >
            {status === 'submitting' ? <Loader2 className="animate-spin" size={20} /> : null}
            {status === 'submitting' ? t('order:rate.submitting') : t('order:rate.submit')}
          </button>
        </form>
      </div>
    </div>
    <SiteFooter />
    </>
  );
};

export default RateOrder;
